import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, MenuProps, message, Space, Table, Tooltip } from 'antd';
import { buildInQuery, CustomerStatusEnumerator, CustomerTypeEnum, IItemsWithCount, IUserEntity, KeyValueRecord, LeadCreditStatusEnum } from '@rasayi-workspace/shared';
import { DEFAULT_TABLE_STATE } from '@constants';
import { ITableFilters, ITableState } from '@interfaces';
import { ColumnsType } from 'antd/es/table';
import { EMPTY_INITIAL_ITEMS, GetTableItems } from '@services';
import TextTypography from 'src/app/components/shared/Typography/Text';
import { TableProps } from 'antd/lib';
import { MapAntDesignSearchFilter, MapAntDesignSortOrder, RemoveUndefinedKeyPairs } from '@helpers';
import { SorterResult } from 'antd/es/table/interface';
import MoreIcon from 'src/assets/icons/moreIcon';
import { EditOutlined, StopOutlined, UserOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import FilterBar from 'src/app/components/shared/filterBar';
import { UserAddEditModalComponent } from 'src/app/components/shared/Customer/AddEditModal';
import { useCustomerContext } from 'src/app/contexts/customer';
import { HoldModal } from 'src/app/components/shared/modals/holdModal';

interface IFilterItemProps {
    children: ReactNode;
    item: IUserEntity;
}
interface ICreditLeadsProps {
    customerStatus?: CustomerStatusEnumerator;
    customerType: CustomerTypeEnum[];
    activeTab: string;
    tabKey: string;
    savedFilters: ITableFilters;
    setSavedFilters: (data: ITableFilters) => void;
}

export const CustomersTable = ({ activeTab, tabKey, setSavedFilters, savedFilters, customerType, customerStatus }: ICreditLeadsProps) => {
    const { date } = useCustomerContext();
    const [holdModal, setHoldModal] = useState('');
    const [modalType, setModalType] = useState<'hold' | 'accept' | 'reject'>('hold');
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const [messageApi, contextHolder] = message.useMessage();
    const [customersData, setCustomersData] = useState<IItemsWithCount<IUserEntity>>(EMPTY_INITIAL_ITEMS);
    const [isUserModalOpened, setIsUserModalOpened] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [editMode, setEditMode] = useState(false);

    const [tableState, setTableState] = useState<ITableState>({
        ...DEFAULT_TABLE_STATE,
        sortBy: { created_at: 'DESC' },
    });

    const refetch = useCallback(
        async (tableStateParams: ITableState, searchParams?: string, qyeryFilter?: string) => {
            setIsFetching(true);
            try {
                let queryParts: string[] = [];

                queryParts.push(buildInQuery('customer_type', customerType));
                const customerQuery = queryParts.join('').replace(/;+$/, '');

                let query = `filter=roles.name,eq,customer;${customerQuery}`;
                if (customerStatus) query += `;customer_status,eq,${customerStatus}`;

                query = `${query}${searchParams ? `;${searchParams}` : ''}${qyeryFilter || ''}`;
                const returnedResult: IItemsWithCount<IUserEntity> = await GetTableItems<IUserEntity>(`user?${query}`, ['roles', 'leads', 'leads.credit'], [], tableStateParams);
                setCustomersData(returnedResult);
            } catch (error) {
                setCustomersData(EMPTY_INITIAL_ITEMS);
            } finally {
                setIsFetching(false);
            }
        },
        [customerType, customerStatus]
    );

    const handleChange: TableProps<IUserEntity>['onChange'] = (pagination, filters, sorter) => {
        let sortBy: KeyValueRecord = RemoveUndefinedKeyPairs({
            [(sorter as SorterResult<IUserEntity>).columnKey as keyof IUserEntity]: MapAntDesignSortOrder((sorter as SorterResult<IUserEntity>).order),
        });

        sortBy = Object.keys(sortBy).length ? sortBy : DEFAULT_TABLE_STATE.sortBy;

        setTableState({
            ...tableState,
            page: pagination.current || tableState.page,
            pageSize: pagination.pageSize || tableState.pageSize,
            sortBy: sortBy,
            search: MapAntDesignSearchFilter(RemoveUndefinedKeyPairs(filters)),
        });
    };

    const columns: ColumnsType<IUserEntity> = [
        {
            title: 'User ID',
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[134px]">
                    <Tooltip title={_}>{_}</Tooltip>
                </TextTypography>
            ),
        },

        {
            title: 'First Name',
            dataIndex: 'first_name',
            key: 'first_name',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[120px]">
                    <Tooltip title={_}>{_}</Tooltip>
                </TextTypography>
            ),
        },
        {
            title: 'Last Name',
            dataIndex: 'last_name',
            key: 'last_name',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[120px]">
                    <Tooltip title={_}>{_}</Tooltip>
                </TextTypography>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[218px]">
                    <Tooltip title={_}>{_}</Tooltip>
                </TextTypography>
            ),
        },
        {
            title: 'Phone Number',
            dataIndex: 'primary_mobile',
            key: 'primary_mobile',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[159px]">
                    <Tooltip title={_}>{_}</Tooltip>
                </TextTypography>
            ),
        },
        {
            title: 'CNIC',
            dataIndex: 'cnic_number',
            key: 'cnic_number',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[160px]">
                    <Tooltip title={_}>{_}</Tooltip>
                </TextTypography>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'customer_status',
            key: 'customer_status',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[160px]">
                    <Tooltip title={_}>{_}</Tooltip>
                </TextTypography>
            ),
        },
        {
            title: 'Province',
            dataIndex: 'province',
            key: 'province',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[160px]">
                    <Tooltip title={_}>{_}</Tooltip>
                </TextTypography>
            ),
        },
        {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[160px]">
                    <Tooltip title={_}> {_}</Tooltip>
                </TextTypography>
            ),
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[160px]">
                    <Tooltip title={_}>{_}</Tooltip>
                </TextTypography>
            ),
        },

        {
            title: 'Actions',
            dataIndex: 'operation',
            align: 'right',
            fixed: 'right',
            key: 'operation',
            render: (_: string, item: IUserEntity) => (
                <div className="w-[60px] flex justify-end">
                    <ActionModal item={item as IUserEntity}>
                        <Button type="text" shape="circle" icon={<MoreIcon />} className="!text-end h-[40px] w-[20px]" />
                    </ActionModal>
                </div>
            ),
        },
    ];

    const ActionModal: React.FC<IFilterItemProps> = ({ children, item }: IFilterItemProps) => {
        const shouldShowHoldOption = item.leads?.length === 1;
        const leadId = item.leads?.[0]?.id;
        const items: MenuProps['items'] = [
            {
                label: (
                    <div onClick={() => {}} className="flex items-center gap-2">
                        <UserOutlined className="w-[20px] h-[20px] " />
                        <Link to={`/customer-detail/${item.id}`}>
                            {' '}
                            <TextTypography fontSize={14}>View Customer Details</TextTypography>
                        </Link>
                    </div>
                ),
                key: 'approved',
            },
            {
                label: (
                    <div
                        onClick={() => {
                            setSelectedCustomerId(item.id);
                            setEditMode(true);
                        }}
                        className="flex items-center gap-2"
                    >
                        <EditOutlined className="w-[20px] h-[20px]" />

                        <TextTypography fontSize={14}>Edit Customer Details</TextTypography>
                    </div>
                ),
                key: 'rejected',
            },

            ...(shouldShowHoldOption && leadId
                ? [
                      {
                          label: (
                              <div
                                  onClick={() => {
                                      setHoldModal(leadId);
                                      setModalType('hold');
                                  }}
                                  className="flex items-center gap-2"
                              >
                                  <StopOutlined className="w-[20px] h-[20px]" />
                                  <TextTypography fontSize={14}>Move to Hold</TextTypography>
                              </div>
                          ),
                          key: 'hold',
                      },
                  ]
                : []),
        ];

        return (
            <div>
                <Dropdown menu={{ items }} trigger={['click']} placement="topLeft" overlayStyle={{ marginLeft: '0px' }}>
                    <a onClick={e => e.preventDefault()}>
                        <Space wrap direction="vertical">
                            {children}
                        </Space>
                    </a>
                </Dropdown>
            </div>
        );
    };

    const getData = useCallback(() => {
        return debounce((tableStateParams: ITableState) => {
            const filters = { ...savedFilters };
            let queryParts: string[] = [];

            queryParts.push(buildInQuery('city', filters.city));
            queryParts.push(buildInQuery('province', filters.province));
            queryParts.push(buildInQuery('customer_status', filters.customerStatus));

            if (filters.quantity !== null && filters.quantity !== undefined) {
                queryParts.push(`products.quantity,eq,${filters.quantity}:`);
            }
            // Date filters
            if (filters?.createdAt) {
                queryParts.push(`created_at,ge,${filters.createdAt}`);
            }

            // Join parts and trim extra colons
            let query = queryParts.join('').replace(/;+$/, '');

            let manualilter = '&ignoreHoldCustomer=true';
            if (savedFilters.searchedText) manualilter += `&search_text=${savedFilters.searchedText}`;
            if (date?.startDate && date?.endDate) manualilter += `&startDate=${date.startDate}&endDate=${date.endDate}`;

            refetch(tableStateParams, query, manualilter);
        }, 1000);
    }, [savedFilters, date?.startDate, date?.endDate]);

    useEffect(() => {
        if (tabKey === activeTab) {
            getData()(tableState);
        }
    }, [activeTab, getData, tableState.page, tableState.search, tableState.sortBy]);

    const addEditModalOpen = useMemo(() => {
        return isUserModalOpened || (editMode && selectedCustomerId);
    }, [editMode, isUserModalOpened, selectedCustomerId]);

    const onClearFieldsHandler = () => {
        if (selectedCustomerId) setSelectedCustomerId('');
        if (editMode) setEditMode(false);
        if (isUserModalOpened) setIsUserModalOpened(false);
    };

    return (
        <>
            {contextHolder}
            <FilterBar
                showSearch={true}
                showFilterButton={true}
                showAddButton={true}
                onSearch={() => {}}
                onFilterClick={() => {}}
                setLeadModalVisible={() => {}}
                leadModalVisible={false}
                onRemoveFilter={() => {}}
                refetchWithFilters={setSavedFilters}
                AddButton={
                    <div>
                        <Button
                            className="bg-[#BD273B] text-white hover:!text-white hover:!border-[#BD273B] px-5 rounded-lg w-[125px] h-[40px]"
                            onClick={() => {
                                setIsUserModalOpened(true);
                            }}
                        >
                            Add Customer
                        </Button>
                    </div>
                }
                filtersToShow={['province', 'city', 'created_at', 'customer_status']}
            />

            <div className="border-2 border-black rounded-lg">
                <Table
                    columns={columns}
                    dataSource={customersData.items}
                    onChange={handleChange}
                    pagination={{
                      total: customersData.count,
                      pageSize: customersData.pageSize,
                      current: customersData.page,
                      size: 'default',
                      className: 'ant-pagination-centered',
                    }}
                    loading={isFetching}
                    rowKey={'id'}
                    size="small"
                    scroll={{ x: true }}
                />
            </div>
            {addEditModalOpen && (
                <UserAddEditModalComponent
                    editMode={editMode}
                    id={selectedCustomerId}
                    onClose={onClearFieldsHandler}
                    onSubmit={() => {
                        getData()(tableState);
                        onClearFieldsHandler();
                    }}
                />
            )}

            {holdModal ? (
                <HoldModal
                    modalType={modalType}
                    visible={holdModal}
                    onClose={() => {
                        setHoldModal('');
                    }}
                    refetch={() => {
                        getData()(DEFAULT_TABLE_STATE);
                    }}
                />
            ) : null}
        </>
    );
};
